import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Clock, CheckCircle, Image as ImageIcon, FileText, Eye, Paperclip, Calendar, RotateCcw } from 'lucide-react';
import api from '../services/api';
import { useToast } from '../hooks/use-toast';
import { formatLocalDate, formatLocalDateTime, isPast, isToday } from '../utils/dateUtils';

const PendingWorkPage = () => {
  const [subjects, setSubjects] = useState([]);
  const [pendingWork, setPendingWork] = useState([]);
  const [allPendingWork, setAllPendingWork] = useState([]);
  const [children, setChildren] = useState([]);
  const [selectedChildId, setSelectedChildId] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selectedWork, setSelectedWork] = useState(null);
  const [isGrading, setIsGrading] = useState(false);
  const [isViewingSubmission, setIsViewingSubmission] = useState(false);
  const [isSendingBack, setIsSendingBack] = useState(false);
  const { toast } = useToast();
  const [gradeData, setGradeData] = useState({
    pointsAwarded: '',
    notes: '',
    tokens: 0  // Default to 0 - parent must manually specify
  });
  const [sendBackNotes, setSendBackNotes] = useState('');

  useEffect(() => {
    // Ensure token is set before loading data
    const token = localStorage.getItem('access_token');
    if (token) {
      api.setToken(token);
      loadData();
    } else {
      setLoading(false);
    }
  }, []);

  const loadData = async () => {
    try {
      const [subjectsData, pendingData, childrenData] = await Promise.all([
        api.getSubjects(),
        api.getPendingAssignments(),
        api.getChildren()
      ]);
      setSubjects(subjectsData);
      setAllPendingWork(pendingData);
      setPendingWork(pendingData);
      setChildren(childrenData);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load pending work',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Filter pending work by selected child
  useEffect(() => {
    if (selectedChildId === 'all') {
      setPendingWork(allPendingWork);
    } else {
      setPendingWork(allPendingWork.filter(work => work.child_id === selectedChildId));
    }
  }, [selectedChildId, allPendingWork]);

  const getSubject = (subjectId) => subjects.find(s => s.id === subjectId);

  // Helper to check if submitted on time (considers same day as on time)
  const isSubmittedOnTime = (submittedDate, dueDate) => {
    const due = new Date(dueDate);
    const submitted = new Date(submittedDate);
    
    // Compare only the date parts (ignore time) in local timezone
    const dueLocalDate = new Date(due.getFullYear(), due.getMonth(), due.getDate(), 23, 59, 59, 999);
    const submittedLocalDate = new Date(submitted.getFullYear(), submitted.getMonth(), submitted.getDate(), submitted.getHours(), submitted.getMinutes(), submitted.getSeconds());
    
    return submittedLocalDate <= dueLocalDate;
  };

  const handleViewSubmission = (work) => {
    setSelectedWork(work);
    setIsViewingSubmission(true);
  };

  const handleGradeWork = (work) => {
    setSelectedWork(work);
    setGradeData({
      pointsAwarded: '',
      notes: '',
      tokens: 0  // Default to 0 - parent must manually specify
    });
    setIsGrading(true);
  };

  const handleApprove = async () => {
    try {
      const pointsAwarded = parseFloat(gradeData.pointsAwarded);
      const pointsPossible = parseFloat(selectedWork.points_possible);
      
      // Grade the assignment via API
      await api.gradeAssignment(selectedWork.id, {
        assignment_id: selectedWork.id,
        points_awarded: pointsAwarded,
        notes: gradeData.notes,
        tokens_awarded: parseInt(gradeData.tokens)
      });

      const percentage = (pointsAwarded / pointsPossible) * 100;
      const bonusPoints = pointsAwarded > pointsPossible ? pointsAwarded - pointsPossible : 0;
      
      const description = bonusPoints > 0 
        ? `${selectedWork.title} has been graded ${pointsAwarded}/${pointsPossible} (${Math.round(percentage)}%) with ${bonusPoints} bonus points and ${gradeData.tokens} tokens awarded.`
        : `${selectedWork.title} has been graded ${pointsAwarded}/${pointsPossible} (${Math.round(percentage)}%) and ${gradeData.tokens} tokens awarded.`;

      toast({
        title: 'Work Approved!',
        description
      });

      // Reload pending work
      await loadData();
      
      setIsGrading(false);
      setSelectedWork(null);
    } catch (error) {
      console.error('Failed to grade assignment:', error);
      toast({
        title: 'Error',
        description: 'Failed to grade assignment',
        variant: 'destructive'
      });
    }
  };

  const handleSendBackClick = (work) => {
    setSelectedWork(work);
    setSendBackNotes('');
    setIsSendingBack(true);
  };

  const handleSendBackConfirm = async () => {
    try {
      await api.sendBackAssignment(selectedWork.id, sendBackNotes);

      toast({
        title: 'Assignment Sent Back',
        description: `${selectedWork.title} has been sent back to ${selectedWork.child_name} for resubmission.`
      });

      // Reload pending work
      await loadData();
      
      setIsSendingBack(false);
      setSelectedWork(null);
      setSendBackNotes('');
    } catch (error) {
      console.error('Failed to send back assignment:', error);
      toast({
        title: 'Error',
        description: 'Failed to send back assignment',
        variant: 'destructive'
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50 flex items-center justify-center">
        <div className="text-xl text-slate-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50">
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Pending Submissions</h1>
          <p className="text-slate-600 text-lg">Review and grade student work</p>
        </div>

        {/* Student Filter */}
        {children.length > 1 && (
          <Card className="p-4 mb-6 border-0 shadow-md bg-white">
            <div className="flex items-center gap-4">
              <Label className="text-sm font-semibold text-slate-700 whitespace-nowrap">Filter by Student:</Label>
              <Select value={selectedChildId} onValueChange={setSelectedChildId}>
                <SelectTrigger className="w-64">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Students ({allPendingWork.length})</SelectItem>
                  {children.map(child => {
                    const childCount = allPendingWork.filter(w => w.child_id === child.id).length;
                    return (
                      <SelectItem key={child.id} value={child.id}>
                        {child.name} ({childCount})
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </Card>
        )}

        {pendingWork.length === 0 ? (
          <Card className="p-12 text-center border-0 shadow-lg">
            <Clock className="w-24 h-24 text-slate-300 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-slate-900 mb-4">All Caught Up!</h2>
            <p className="text-slate-600">No pending submissions to review at this time.</p>
          </Card>
        ) : (
          <div className="space-y-6">
            {pendingWork.map(work => {
              const subject = getSubject(work.subject_id);
              return (
                <Card key={work.id} className="p-6 border-0 shadow-lg hover:shadow-xl transition-all bg-white">
                  <div className="flex items-start justify-between gap-6">
                    <div className="flex-1">
                      {/* Subject Badge */}
                      {subject && (
                        <div 
                          className="inline-block px-3 py-1 rounded-full text-sm font-medium mb-3"
                          style={{ backgroundColor: `${subject.color}20`, color: subject.color }}
                        >
                          {subject.icon} {subject.name}
                        </div>
                      )}

                      {/* Title & Student */}
                      <h3 className="text-xl font-bold text-slate-900 mb-2">{work.title}</h3>
                      <p className="text-slate-600 mb-3">Student: {work.child_name}</p>

                      {/* Description */}
                      <p className="text-slate-700 mb-4">{work.description}</p>

                      {/* Submission Info */}
                      {work.submission_description && (
                        <div className="bg-blue-50 p-3 rounded-lg mb-4">
                          <Label className="text-sm font-semibold text-blue-900">Submission Notes:</Label>
                          <p className="text-sm text-blue-800 mt-1">{work.submission_description}</p>
                        </div>
                      )}

                      {/* Metadata */}
                      <div className="flex items-center gap-4 text-sm text-slate-500 flex-wrap">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4 text-slate-600" />
                          <span className="text-slate-900 font-medium">Due: {formatLocalDateTime(work.due_date)}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className={`w-4 h-4 ${isSubmittedOnTime(work.submitted_date, work.due_date) ? 'text-green-600' : 'text-red-600'}`} />
                          <span className={`font-medium ${isSubmittedOnTime(work.submitted_date, work.due_date) ? 'text-green-600' : 'text-red-600'}`}>
                            Submitted: {formatLocalDateTime(work.submitted_date)}
                          </span>
                        </div>
                        <Badge variant="outline" className="text-sm">
                          {work.points_possible} points possible
                        </Badge>
                        {work.attachments && work.attachments.length > 0 && (
                          <div className="flex items-center gap-1">
                            <Paperclip className="w-4 h-4" />
                            {work.attachments.length} attachment{work.attachments.length > 1 ? 's' : ''}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2">
                      <Button
                        onClick={() => handleViewSubmission(work)}
                        variant="outline"
                        className="w-full"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        View Details
                      </Button>
                      <Button
                        onClick={() => handleGradeWork(work)}
                        className="w-full bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Grade Work
                      </Button>
                      <Button
                        onClick={() => handleSendBackClick(work)}
                        variant="outline"
                        className="w-full border-orange-300 text-orange-600 hover:bg-orange-50 hover:text-orange-700"
                      >
                        <RotateCcw className="w-4 h-4 mr-2" />
                        Send Back
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* View Submission Dialog */}
        <Dialog open={isViewingSubmission} onOpenChange={setIsViewingSubmission}>
          <DialogContent className="bg-white max-w-2xl max-h-[90vh] flex flex-col">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle className="text-2xl font-bold text-slate-900">Submission Details</DialogTitle>
            </DialogHeader>
            {selectedWork && (
              <div className="space-y-6 mt-4 overflow-y-auto flex-1 pr-2">
                {/* Subject */}
                {(() => {
                  const subject = getSubject(selectedWork.subject_id);
                  return subject ? (
                    <div 
                      className="inline-block px-4 py-2 rounded-lg text-base font-medium"
                      style={{ backgroundColor: `${subject.color}20`, color: subject.color }}
                    >
                      {subject.icon} {subject.name}
                    </div>
                  ) : null;
                })()}

                {/* Title */}
                <div>
                  <h3 className="text-2xl font-bold text-slate-900">{selectedWork.title}</h3>
                  <p className="text-slate-600 mt-1">Student: {selectedWork.child_name}</p>
                </div>

                {/* Dates */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                    <div className="flex items-center gap-2 mb-1">
                      <Calendar className="w-4 h-4 text-slate-600" />
                      <Label className="text-sm font-semibold text-slate-700">Due Date</Label>
                    </div>
                    <p className="text-lg font-bold text-slate-900">
                      {new Date(selectedWork.due_date).toLocaleDateString()}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg border ${isSubmittedOnTime(selectedWork.submitted_date, selectedWork.due_date) ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className={`w-4 h-4 ${isSubmittedOnTime(selectedWork.submitted_date, selectedWork.due_date) ? 'text-green-600' : 'text-red-600'}`} />
                      <Label className={`text-sm font-semibold ${isSubmittedOnTime(selectedWork.submitted_date, selectedWork.due_date) ? 'text-green-900' : 'text-red-900'}`}>
                        Submitted {isSubmittedOnTime(selectedWork.submitted_date, selectedWork.due_date) ? 'On Time' : 'Late'}
                      </Label>
                    </div>
                    <p className={`text-lg font-bold ${isSubmittedOnTime(selectedWork.submitted_date, selectedWork.due_date) ? 'text-green-600' : 'text-red-600'}`}>
                      {new Date(selectedWork.submitted_date).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                {/* Assignment Description */}
                <div>
                  <Label className="text-lg font-semibold">Assignment</Label>
                  <p className="mt-2 text-slate-700">{selectedWork.description}</p>
                </div>

                {/* Submission Notes */}
                {selectedWork.submission_description && (
                  <div>
                    <Label className="text-lg font-semibold">Submission Notes</Label>
                    <p className="mt-2 text-slate-700 bg-blue-50 p-3 rounded-lg">{selectedWork.submission_description}</p>
                  </div>
                )}

                {/* Points Possible */}
                <div className="bg-slate-50 p-4 rounded-lg">
                  <Label className="text-lg font-semibold">Points Possible</Label>
                  <p className="text-2xl font-bold text-slate-900 mt-2">{selectedWork.points_possible}</p>
                </div>

                {/* Attachments */}
                {selectedWork.attachments && selectedWork.attachments.length > 0 && (
                  <div>
                    <Label className="text-lg font-semibold mb-3 block">Attachments ({selectedWork.attachments.length})</Label>
                    <div className="space-y-3">
                      {selectedWork.attachments.map((attachment, idx) => (
                        <div key={idx} className="bg-slate-50 rounded-lg overflow-hidden border border-slate-200">
                          {attachment.type === 'image' && attachment.url ? (
                            <div className="space-y-2">
                              <div className="aspect-video bg-slate-100 relative">
                                <img 
                                  src={attachment.url} 
                                  alt={attachment.name}
                                  className="w-full h-full object-contain cursor-pointer hover:opacity-90 transition-opacity"
                                  onClick={() => window.open(attachment.url, '_blank')}
                                />
                              </div>
                              <div className="p-3 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <ImageIcon className="w-4 h-4 text-blue-500" />
                                  <span className="text-sm text-slate-700">{attachment.name}</span>
                                </div>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => window.open(attachment.url, '_blank')}
                                  className="text-blue-600 hover:text-blue-700"
                                >
                                  View Full Size
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div className="p-3 flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <FileText className="w-5 h-5 text-slate-500" />
                                <span className="text-sm text-slate-700">{attachment.name}</span>
                              </div>
                              {attachment.url && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => window.open(attachment.url, '_blank')}
                                  className="text-blue-600 hover:text-blue-700"
                                >
                                  Download
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              </div>
            )}
            {/* Action Buttons - Always visible at bottom */}
            {selectedWork && (
              <div className="flex gap-3 pt-4 border-t border-slate-200 flex-shrink-0">
                <Button
                  onClick={() => {
                    setIsViewingSubmission(false);
                    handleSendBackClick(selectedWork);
                  }}
                  variant="outline"
                  className="flex-1 border-orange-300 text-orange-600 hover:bg-orange-50 hover:text-orange-700"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Send Back
                </Button>
                <Button
                  onClick={() => {
                    setIsViewingSubmission(false);
                    handleGradeWork(selectedWork);
                  }}
                  className="flex-1 bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Grade Work
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Grading Dialog */}
        <Dialog open={isGrading} onOpenChange={setIsGrading}>
          <DialogContent className="bg-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-slate-900">Grade Assignment</DialogTitle>
            </DialogHeader>
            {selectedWork && (
              <div className="space-y-6 mt-4">
                <div>
                  <h4 className="font-bold text-lg text-slate-900">{selectedWork.title}</h4>
                  <p className="text-slate-600">Student: {selectedWork.child_name}</p>
                </div>

                <div className="bg-slate-100 p-4 rounded-lg">
                  <Label className="text-lg font-semibold mb-3 block">Points</Label>
                  <div className="flex items-center gap-2 text-lg">
                    <Input
                      type="number"
                      placeholder="0"
                      value={gradeData.pointsAwarded}
                      onChange={(e) => setGradeData({ ...gradeData, pointsAwarded: e.target.value })}
                      className="w-24 text-center text-lg font-bold"
                      min="0"
                      step="0.5"
                    />
                    <span className="text-slate-600 font-medium">/ {selectedWork.points_possible}</span>
                    {gradeData.pointsAwarded && (
                      <>
                        <Badge className="ml-2 bg-blue-100 text-blue-700">
                          {Math.round((parseFloat(gradeData.pointsAwarded) / selectedWork.points_possible) * 100)}%
                        </Badge>
                        {parseFloat(gradeData.pointsAwarded) > selectedWork.points_possible && (
                          <Badge className="bg-green-100 text-green-700 border-green-300">
                            +{(parseFloat(gradeData.pointsAwarded) - selectedWork.points_possible).toFixed(1)} Bonus
                          </Badge>
                        )}
                      </>
                    )}
                  </div>
                  {gradeData.pointsAwarded && parseFloat(gradeData.pointsAwarded) > selectedWork.points_possible && (
                    <p className="text-sm text-green-600 mt-2">
                      Bonus points will count towards subject average
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="tokens">Tokens to Award</Label>
                  <Input
                    id="tokens"
                    type="number"
                    min="0"
                    value={gradeData.tokens}
                    onChange={(e) => setGradeData({ ...gradeData, tokens: e.target.value })}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="notes">Teacher Notes (Optional)</Label>
                  <Textarea
                    id="notes"
                    placeholder="Great work! Keep it up..."
                    value={gradeData.notes}
                    onChange={(e) => setGradeData({ ...gradeData, notes: e.target.value })}
                    className="mt-2 min-h-[100px]"
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={() => setIsGrading(false)}
                    variant="outline"
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleApprove}
                    disabled={!gradeData.pointsAwarded || parseFloat(gradeData.pointsAwarded) < 0}
                    className="flex-1 bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Submit Grade
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Send Back Dialog */}
        <Dialog open={isSendingBack} onOpenChange={setIsSendingBack}>
          <DialogContent className="bg-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-slate-900">Send Back for Resubmission</DialogTitle>
            </DialogHeader>
            {selectedWork && (
              <div className="space-y-6 mt-4">
                <div>
                  <h4 className="font-bold text-lg text-slate-900">{selectedWork.title}</h4>
                  <p className="text-slate-600">Student: {selectedWork.child_name}</p>
                </div>

                <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg">
                  <p className="text-sm text-orange-800">
                    This assignment will be sent back to the student for resubmission. 
                    The student will be able to see your notes and submit updated work.
                  </p>
                </div>

                <div>
                  <Label htmlFor="sendBackNotes">Notes to Student (Optional)</Label>
                  <Textarea
                    id="sendBackNotes"
                    placeholder="Please review the instructions and resubmit..."
                    value={sendBackNotes}
                    onChange={(e) => setSendBackNotes(e.target.value)}
                    className="mt-2 min-h-[120px]"
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={() => setIsSendingBack(false)}
                    variant="outline"
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSendBackConfirm}
                    className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Send Back
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default PendingWorkPage;
